import { createFileRoute, Outlet } from '@tanstack/react-router';
import { createLogger } from '@/utils/logger';
import { z } from 'zod';
import { TaxBinderDetailsPage } from '@/features/preparer/tax-binders/TaxBinderDetailsPage';
import {
  taxBinderQueryOptions,
  documentQueryOptions,
  questionnaireQueryOptions,
  taxReturnQueryOptions,
} from '@/lib/query/options';
import { QueryErrorBoundary } from '@/components/query/QueryErrorBoundary';
import { TaxBinderTab } from '@/types/taxBinderTabs';

const binderSearchSchema = z.object({
  tab: z
    .enum([
      TaxBinderTab.DocumentRequests,
      TaxBinderTab.ClientDocuments,
      TaxBinderTab.FirmDocuments,
      TaxBinderTab.Questionnaire,
      TaxBinderTab.Comments,
      TaxBinderTab.InternalNotes,
      TaxBinderTab.AuditLog,
      TaxBinderTab.TaxReturns,
      TaxBinderTab.Signatures,
      TaxBinderTab.K1Distribution,
    ])
    .optional()
    .catch(TaxBinderTab.DocumentRequests),
  openCchImport: z.boolean().optional().catch(undefined),
});

const log = createLogger('PreparerBinderRoute');

export const Route = createFileRoute('/preparer/tax-binders/$binderId')({
  validateSearch: binderSearchSchema,
  loader: async ({ context: { queryClient }, params: { binderId } }) => {
    // Get auth state from query cache
    const authState = queryClient.getQueryData(['auth', 'session']) as { token?: string } | undefined;
    const token = authState?.token;

    if (token && binderId) {
      // Prefetch binder details - Don't throw errors, let the component handle them
      // Use prefetchQuery instead of ensureQueryData to avoid throwing to error boundary
      try {
        const binder = await queryClient.fetchQuery(taxBinderQueryOptions.detail(binderId, token));

        // Background prefetch documents for this binder (non-blocking)
        // This must match the query used in TaxBinderDetailsPage.tsx
        queryClient.prefetchQuery(documentQueryOptions.byBinder(binderId, {}, token));

        // Background prefetch questionnaire pre-fill data for questionnaire pre-population (non-blocking)
        // Only prefetch if we have a valid clientId
        // This replaces the previous clientQueryOptions.detail prefetch
        if (binder?.clientId) {
          queryClient.prefetchQuery(questionnaireQueryOptions.preFillData(binder.clientId, binderId, token));
        }

        // Background prefetch tax returns for the binder (non-blocking)
        queryClient.prefetchQuery(taxReturnQueryOptions.list(binderId, token));
      } catch (error) {
        // Silently catch errors (403, 404, etc.) and let the component handle them
        // The component will show appropriate error messages based on the error type
        log.debug('Binder prefetch failed, will be handled by component:', error);
      }
    }
  },
  errorComponent: QueryErrorBoundary,
  component: BinderDetailsLayout,
});

function BinderDetailsLayout() {
  return (
    <>
      <TaxBinderDetailsPage />
      <Outlet />
    </>
  );
}
